// EverValue Chart Configuration - Dark Theme with Orange Accents
import { Config, Layout } from 'plotly.js';

// Base dark theme layout for all charts
export const getBaseLayout = (title: string): Partial<Layout> => ({
  title: { text: '' },
  paper_bgcolor: 'rgba(0,0,0,0)',
  plot_bgcolor: 'rgba(0,0,0,0)',
  font: {
    family: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    size: 12,
    color: '#e0e0e0'
  },
  margin: { t: 20, r: 50, b: 80, l: 60 },
  showlegend: true,
  legend: {
    bgcolor: 'rgba(26, 26, 26, 0.9)',
    bordercolor: '#404040',
    borderwidth: 1,
    font: {
      color: '#e0e0e0',
      size: 10
    },
    orientation: 'h',
    x: 0.5,
    xanchor: 'center',
    y: -0.25,
    tracegroupgap: 8,
    itemwidth: 30
  },
  hoverlabel: {
    bgcolor: '#1a1a1a',
    bordercolor: '#ff8c00',
    font: {
      color: '#ffffff',
      size: 12
    }
  }
});

// Line chart specific configuration
export const getLineChartLayout = (title: string, xAxisTitle: string, yAxisTitle: string): Partial<Layout> => ({
  ...getBaseLayout(title),
  margin: { t: 20, r: 50, b: 140, l: 80 }, // Increased left margin to accommodate y-axis label with standoff
  xaxis: {
    title: {
      text: xAxisTitle,
      font: { color: '#e0e0e0', size: 14 },
      standoff: 10
    },
    gridcolor: '#404040',
    gridwidth: 1,
    tickfont: { color: '#b0b0b0', size: 10 },
    linecolor: '#606060',
    linewidth: 1,
    zeroline: false
  },
  yaxis: {
    title: {
      text: yAxisTitle,
      font: { color: '#e0e0e0', size: 14 },
      standoff: 20 // Add proper spacing between y-axis label and tick values
    },
    gridcolor: '#404040',
    gridwidth: 1,
    tickfont: { color: '#b0b0b0', size: 10 },
    linecolor: '#606060',
    linewidth: 1,
    zeroline: false,
    tickformat: '$,.0s' // Use 's' suffix format (K, M, B) instead of full numbers
  },
  hovermode: 'x unified'
});

// Pie chart specific configuration
export const getPieChartLayout = (title: string): Partial<Layout> => ({
  ...getBaseLayout(title),
  margin: { t: 20, r: 200, b: 60, l: 60 }, // Reduced top margin since no internal title
  height: 450, // Increased height for better proportions
  showlegend: true,
  legend: {
    bgcolor: 'rgba(26, 26, 26, 0.9)',
    bordercolor: '#404040',
    borderwidth: 1,
    font: {
      color: '#e0e0e0',
      size: 11
    },
    orientation: 'v',
    x: 1.05,
    xanchor: 'left',
    y: 0.5,
    yanchor: 'middle',
    tracegroupgap: 5
  }
});

// Bar chart specific configuration
export const getBarChartLayout = (title: string, xAxisTitle: string, yAxisTitle: string): Partial<Layout> => ({
  ...getBaseLayout(title),
  margin: { t: 10, r: 20, b: 60, l: 70 }, // Reduced margins to maximize chart space
  height: 450, // Increased height to match pie chart better
  showlegend: false, // Hide legend for bar charts
  xaxis: {
    title: {
      text: xAxisTitle,
      font: { color: '#e0e0e0', size: 14 },
      standoff: 5 // Reduced standoff
    },
    gridcolor: '#404040',
    gridwidth: 1,
    tickfont: { color: '#b0b0b0', size: 10 },
    linecolor: '#606060',
    linewidth: 1,
    zeroline: false
  },
  yaxis: {
    title: {
      text: yAxisTitle,
      font: { color: '#e0e0e0', size: 14 },
      standoff: 15 // Reduced standoff
    },
    gridcolor: '#404040',
    gridwidth: 1,
    tickfont: { color: '#b0b0b0', size: 10 },
    linecolor: '#606060',
    linewidth: 1,
    zeroline: false,
    tickformat: '$,.0s' // Use 's' suffix format (K, M, B) instead of full numbers
  },
  hovermode: 'closest'
});

// Base configuration for all charts
export const getBaseConfig = (): Partial<Config> => ({
  responsive: true,
  displayModeBar: true,
  displaylogo: false,
  modeBarButtonsToRemove: [
    'pan2d',
    'lasso2d',
    'select2d',
    'autoScale2d',
    'hoverClosestCartesian',
    'hoverCompareCartesian',
    'toggleSpikelines'
  ],
  toImageButtonOptions: {
    format: 'png',
    filename: 'evervalue-chart',
    height: 500,
    width: 800,
    scale: 2
  }
});

// EverValue brand colors for charts
export const CHART_COLORS = {
  primary: '#FF8C00',
  secondary: '#FCB900',
  accent: '#FF7700',
  success: '#00d084',
  warning: '#FCB900',
  error: '#cf2e2e',
  info: '#8ed1fc',
  // Extended palette for multiple data series
  palette: [
    '#FF8C00', // Uniswap V3 primary
    '#10B981', // PancakeSwap V3 primary  
    '#3B82F6', // Uniswap V4 primary
    '#F82D5A', // SushiSwap V3 primary
    '#FF7700', // Orange variant
    '#059669', // Green variant
    '#6366F1', // Blue variant
    '#E91E63', // Pink variant
    '#FFA500', // Light orange
    '#047857', // Dark green
    '#8B5CF6', // Purple
    '#FF6B9D'  // Light pink
  ]
};

// Utility function to get chart colors based on data length
export const getChartColors = (count: number): string[] => {
  if (count <= CHART_COLORS.palette.length) {
    return CHART_COLORS.palette.slice(0, count);
  }
  
  // If we need more colors, cycle through the palette
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    colors.push(CHART_COLORS.palette[i % CHART_COLORS.palette.length]);
  }
  return colors;
};

// Animation configuration for smooth transitions
export const getAnimationConfig = () => ({
  transition: {
    duration: 500,
    easing: 'cubic-in-out'
  },
  frame: {
    duration: 500,
    redraw: false
  }
});

// Responsive breakpoints for chart sizing
export const CHART_BREAKPOINTS = {
  mobile: 480,
  tablet: 768,
  desktop: 1024
};

// Get responsive chart dimensions
export const getResponsiveDimensions = (width: number) => {
  if (width <= CHART_BREAKPOINTS.mobile) {
    return { width: '100%', height: '300px' };
  } else if (width <= CHART_BREAKPOINTS.tablet) {
    return { width: '100%', height: '350px' };
  } else {
    return { width: '100%', height: '400px' };
  }
};
